import type {
  CareerLayerApiResponse,
  ConsolidatedByYearsData,
  ModuleEntity,
} from '@/domains/careerLayer/careerLayer.entity';
import type { RiskIndicator } from '@/domains/shared';

/**
 * UseCase para consolidar datos de TODOS los años en un solo objeto
 *
 * Responsabilidades:
 * - Recibir data (ya filtrada por año)
 * - Consolidar módulos de todos los años por jornada (D y V)
 * - Consolidar indicadores de riesgo (suma bajo/medio/alto)
 * - Retornar estructura { D: {...}, V: {...} }
 *
 * Ejemplo:
 * Input: {
 *   "2025": { D: { modulos: [m1, m2], indicadores: [i1] } },
 *   "2024": { D: { modulos: [m3], indicadores: [i2] } }
 * }
 * Output: {
 *   D: { modulos: [m1, m2, m3], indicadores: [i1+i2 consolidado] }
 * }
 */
export class ConsolidateCareerLayerByYearsUseCase {
  /**
   * Ejecuta la consolidación de todos los años
   * @param apiData - Data filtrada por año
   * @returns Objeto consolidado agrupado solo por jornada
   */
  execute(apiData: CareerLayerApiResponse): ConsolidatedByYearsData {
    if (!apiData?.data) {
      return this.createEmptyConsolidated();
    }

    const consolidatedModulosD: ModuleEntity[] = [];
    const consolidatedModulosV: ModuleEntity[] = [];
    const riskIndicatorsD: RiskIndicator[] = [];
    const riskIndicatorsV: RiskIndicator[] = [];

    // Iterar sobre todos los años
    Object.values(apiData.data).forEach((yearData) => {
      // Consolidar Diurno (D)
      if (yearData.D) {
        consolidatedModulosD.push(...(yearData.D.modulos ?? []));
        riskIndicatorsD.push(...(yearData.D.indicadores_de_riesgo ?? []));
      }

      // Consolidar Vespertino (V)
      if (yearData.V) {
        consolidatedModulosV.push(...(yearData.V.modulos ?? []));
        riskIndicatorsV.push(...(yearData.V.indicadores_de_riesgo ?? []));
      }
    });

    return {
      D: {
        // modulos: consolidatedModulosD,
        modulos: this.consolidateModules(consolidatedModulosD),
        indicadores_de_riesgo: this.consolidateRiskIndicators(riskIndicatorsD),
      },
      V: {
        // modulos: consolidatedModulosV,
        modulos: this.consolidateModules(consolidatedModulosV),
        indicadores_de_riesgo: this.consolidateRiskIndicators(riskIndicatorsV),
      },
    };
  }

  private consolidateModules(modules: ModuleEntity[]): ModuleEntity[] {
    const moduleMap = new Map<string, ModuleEntity>();

    modules.forEach((module) => {
      const key = `${module.nombre_modulo}-${module.nombre_docente}`;
      const existing = moduleMap.get(key);

      if (existing) {
        // SUMAR estudiantes del mismo módulo-docente
        existing.cantidad_estudiantes += module.cantidad_estudiantes;
        existing.estudiantes_con_riesgo += module.estudiantes_con_riesgo;
      } else {
        moduleMap.set(key, { ...module });
      }
    });

    return Array.from(moduleMap.values());
  }

  /**
   * Consolida indicadores de riesgo sumando bajo/medio/alto por nombre
   * @param indicators - Array de indicadores a consolidar
   * @returns Array consolidado con suma de niveles
   */
  private consolidateRiskIndicators(
    indicators: RiskIndicator[],
  ): RiskIndicator[] {
    const consolidated = new Map<string, RiskIndicator>();

    indicators.forEach((indicator) => {
      const existing = consolidated.get(indicator.nombre);

      if (existing) {
        // Sumar valores si ya existe
        existing.bajo += indicator.bajo;
        existing.medio += indicator.medio;
        existing.alto += indicator.alto;
      } else {
        // Crear nuevo
        consolidated.set(indicator.nombre, { ...indicator });
      }
    });

    return Array.from(consolidated.values());
  }

  /**
   * Crea estructura vacía para caso sin datos
   */
  private createEmptyConsolidated(): ConsolidatedByYearsData {
    return {
      D: { modulos: [], indicadores_de_riesgo: [] },
      V: { modulos: [], indicadores_de_riesgo: [] },
    };
  }
}
